import { Stack } from "@mantine/core";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { spacing } from "#app/theme/v2/spacing";
import { InputDuration } from "#components/atoms/InputDuration/InputDuration";
import { SelectableCard } from "#components/molecules/SelectableCard/SelectableCard";
import {
  type Duration,
  formatDurationFormula,
  parseDurationFormula,
} from "#modules/onboardingV2/domain/durationFormula";

type DurationFieldProps = {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
};

export const DurationField = ({ value, onChange, disabled }: DurationFieldProps) => {
  const { t } = useTranslation();
  const isManual = value !== null;
  const duration = parseDurationFormula(value);

  const pendingDuration = useRef(duration);
  pendingDuration.current = duration;

  const updateDuration = (next: Partial<Duration>) => {
    pendingDuration.current = { ...pendingDuration.current, ...next };
    onChange(formatDurationFormula(pendingDuration.current));
  };

  return (
    <Stack gap={spacing[3]} style={{ width: "100%" }}>
      <SelectableCard
        title={t("onboardingV2.processStepConfiguration.durationModal.automatic.title")}
        description={t("onboardingV2.processStepConfiguration.durationModal.automatic.description")}
        selected={!isManual}
        disabled={disabled}
        onSelect={() => onChange(null)}
      />
      <SelectableCard
        title={t("onboardingV2.processStepConfiguration.durationModal.manual.title")}
        description={t("onboardingV2.processStepConfiguration.durationModal.manual.description")}
        selected={isManual}
        disabled={disabled}
        onSelect={() => onChange(value ?? "0")}
      />
      {isManual && (
        <InputDuration
          hours={duration.hours}
          minutes={duration.minutes}
          seconds={duration.seconds}
          onHoursChange={(hours) => updateDuration({ hours })}
          onMinutesChange={(minutes) => updateDuration({ minutes })}
          onSecondsChange={(seconds) => updateDuration({ seconds })}
          disabled={disabled}
          showIcon={false}
        />
      )}
    </Stack>
  );
};
